import { api } from '@/clients/admin.client';
import { Link, useRouter } from '@tanstack/react-router';
import {
  ActionIcon,
  Button,
  Center,
  Container,
  Menu,
  Stack,
  Table,
  TextInput,
  Text,
} from '@mantine/core';
import { IconCheck, IconDots, IconEdit, IconTrash } from '@tabler/icons-react';
import { useForm } from 'react-hook-form';
import { notifyError, notifySuccess } from '@/lib/notification';
import { modals } from '@mantine/modals';
import { logger } from '@/lib/logger';
import { fmtDate } from '@/lib/date';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Project } from '@/clients/types';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

type Props = {
  adminClient: typeof api;
  projects: Project[];
};

export function ActiveProjectList({ projects, adminClient }: Props) {
  const router = useRouter();
  const client = useQueryClient();

  const updateProject = useMutation({
    mutationFn: ({ project, inputs }: { project: Project; inputs: Inputs }) =>
      adminClient.admin_v1_update_project(
        {
          name: inputs.name,
        },
        {
          params: { project_id: project.keys.id! },
        }
      ),
    onSuccess: async (_, { project }, _ctx) => {
      client.invalidateQueries({ queryKey: ['projects'] });
      await router.load();
      notifySuccess(`プロジェクト ${project.spec.name} を更新しました。`);
      modals.closeAll();
    },
    onError: (error, { project }, _ctx) => {
      logger.error(error);
      notifyError(`プロジェクト ${project.spec.name} の更新に失敗しました。`);
    },
  });

  const openUpdateProjectModal = (project: Project) => {
    modals.open({
      size: 'md',
      title: 'プロジェクト設定',
      children: (
        <UpdateProjectForm
          onSubmit={(inputs) => updateProject.mutate({ project, inputs })}
          defaultValues={{ name: project.spec.name, id: project.keys.id! }}
        />
      ),
    });
  };

  const archiveProject = useMutation({
    mutationFn: (project: Project) =>
      adminClient.admin_v1_archive_project(undefined, {
        params: { project_id: project.keys.id! },
      }),
    onSuccess: async (_, project, _ctx) => {
      client.invalidateQueries({ queryKey: ['projects'] });
      await router.load();
      notifySuccess(`プロジェクト ${project.spec.name} をアーカイブしました。`);
    },
    onError: (error, project, _ctx) => {
      logger.error(error);
      notifyError(`プロジェクト ${project.spec.name} のアーカイブに失敗しました。`);
    },
  });

  const openArchiveProjectModal = (project: Project) => {
    modals.openConfirmModal({
      title: 'プロジェクトをアーカイブしますか？',
      children: (
        <>
          <Text size="sm">アーカイブしたプロジェクトは元に戻すことができません。</Text>
          <Text size="xs" mt="sm">
            ※
            プロジェクトを削除するには、プロジェクト内のすべてのAPIキーを事前に削除する必要があります。
          </Text>
        </>
      ),
      onConfirm: async () => archiveProject.mutate(project),
    });
  };

  return (
    <Table.ScrollContainer minWidth={700} className="plamo-stack-sm">
      <Table horizontalSpacing="lg">
        <Table.Thead>
          <Table.Tr>
            <Table.Th>名前</Table.Th>
            <Table.Th>作成日</Table.Th>
            <Table.Th>作成者</Table.Th>
            <Table.Th>全員に許可</Table.Th>
            <Table.Th></Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {projects.map((project) => (
            <Table.Tr key={project.keys.id}>
              <Table.Td data-label="名前">
                <Link to="/project" search={{ project: project.keys.id! }}>
                  <Text size="sm" c="blue">
                    {project.spec.name}
                  </Text>
                </Link>
              </Table.Td>
              <Table.Td data-label="作成日">{fmtDate(project.meta?.created_at as string)}</Table.Td>
              <Table.Td data-label="作成者">{project.meta?.created_by}</Table.Td>
              <Table.Td data-label="全員に許可">
                {project.spec.allow_all_users && (
                  <IconCheck style={{ width: '14px', height: '14px' }} />
                )}
              </Table.Td>
              <Table.Td data-action>
                <Menu>
                  <Menu.Target>
                    <ActionIcon variant="outline" size="md">
                      <IconDots style={{ width: '70%', height: '70%' }} stroke={1.5} />
                    </ActionIcon>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Item
                      leftSection={<IconEdit style={{ width: '70%', height: '70%' }} />}
                      onClick={() => {
                        openUpdateProjectModal(project);
                      }}
                    >
                      設定
                    </Menu.Item>
                    <Menu.Item
                      leftSection={
                        <IconTrash
                          style={{ width: '70%', height: '70%' }}
                          className="text-plamo-error"
                        />
                      }
                      onClick={() => {
                        openArchiveProjectModal(project);
                      }}
                      disabled={!!project.spec.allow_all_users}
                    >
                      <div className="text-plamo-error">アーカイブ</div>
                      {!!project.spec.allow_all_users && (
                        <Text size="xs" c="red">
                          デフォルトプロジェクトは
                          <br />
                          アーカイブできません。
                        </Text>
                      )}
                    </Menu.Item>
                  </Menu.Dropdown>
                </Menu>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

const inputSchema = z.object({
  name: z.string().min(1, '必須項目です。').max(63, '63文字以内で入力してください。'),
  id: z.string().min(1),
});
type Inputs = z.infer<typeof inputSchema>;

type FormProps = {
  onSubmit: (data: Inputs) => void;
  defaultValues: Inputs;
};

function UpdateProjectForm({ onSubmit, defaultValues }: FormProps) {
  const {
    register,
    handleSubmit,
    formState: { isValid, errors, dirtyFields },
  } = useForm<Inputs>({
    resolver: zodResolver(inputSchema),
    mode: 'onChange',
    defaultValues,
  });
  const isReady = Object.keys(dirtyFields).length > 0 && isValid;

  return (
    <Container m="lg">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Center>
          <Stack w="100%">
            <TextInput
              label="プロジェクト名"
              placeholder="プロジェクト名"
              {...register('name', {
                required: 'プロジェクト名を入力してください。',
              })}
              error={errors.name?.message}
            />
            <TextInput label="プロジェクト ID" {...register('id')} disabled />
            <Button type="submit" size="sm" disabled={!isReady} mt="lg">
              保存
            </Button>
          </Stack>
        </Center>
      </form>
    </Container>
  );
}
